import { apiClient } from '@/lib/api/client'
import axios from 'axios'

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message
    if (typeof message === 'string' && message.trim() !== '') {
      return message
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

export type ChildTicketTask = {
  id: number
  parent_ticket_id: number
  task_number?: string | null
  title?: string | null
  notes?: string | null
  status?: string | null
  status_label?: string | null
  age_in_status_hours?: number | null
  age_in_status_category?: string | null
  action_type?: string | null
  form_fields?: Array<{
    key: string
    module: string
    label: string
    required?: boolean
    config?: Record<string, unknown>
    requires?: string[]
  }> | null
  form_data?: Record<string, unknown> | null
  priority?: string | null
  due_at?: string | null
  source_field?: string | null
  assigned_to?: number | null
  assignee_name?: string | null
  assigned_team_id?: number | null
  sla_due_at?: string | null
  sla_minutes_override?: number | null
  sort_order?: number | null
  depends_on_task_id?: number | null
  workflow_template_step_id?: number | null
  watchers_count?: number | null
  watchers?: Array<{
    user_id: number
    name: string
  }> | null
  actual_start?: string | null
  actual_end?: string | null
  skipped?: boolean
  skip_reason?: string | null
  skipped_at?: string | null
  skipped_by?: number | null
  reassigned_from_task_id?: number | null
  reassigned_to_task_id?: number | null
  status_changed_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  coordination_metrics?: {
    raw_minutes: number
    paused_minutes: number
    adjusted_minutes: number
    hold_count?: number | null
    linked_fe_visit_task_ids: number[]
  } | null
}

export type ChildTicketPayload = {
  title: string
  status?: string
  assigned_to?: number | null
  notes?: string
  action_type?: string
  source_field?: string
  form_data?: Record<string, unknown>
  form_fields?: ChildTicketTask['form_fields']
}

export type TaskCompletionPreflight = {
  can_complete: boolean
  blockers: Array<{
    type: 'dependencies' | 'checklist' | 'parts'
    message: string
    items: Array<Record<string, unknown>>
  }>
}

export type CloseValidationBlockingTask = {
  id: number
  task_number?: string | null
  title?: string | null
  status?: string | null
}

export type CloseValidationResult = {
  can_close: boolean
  blocking_tasks: CloseValidationBlockingTask[]
}

export type TaskTemplate = {
  id: number
  name: string
  code?: string | null
  description?: string | null
  title_pattern?: string | null
  notes_pattern?: string | null
  default_assignee_rule?: Record<string, unknown> | string | null
  default_priority_rule?: Record<string, unknown> | string | null
  due_date_rule?: Record<string, unknown> | string | null
  allowed_creator_roles?: string[] | null
  task_field_module_code?: string | null
  default_status_code?: string | null
  support_type_id?: number | null
  is_sequential?: boolean
  is_active?: boolean
  checklist_template_id?: number | null
  checklist_template?: { id: number; name: string; items_count?: number | null } | null
  steps?: Array<{
    id: number
    title: string
    status_code: string
    status_label?: string
    action_type?: string | null
    form_fields?: Array<{
      key: string
      module: string
      label: string
      required?: boolean
      config?: Record<string, unknown>
      requires?: string[]
    }>
    default_sla_minutes?: number | null
    assigned_team_id?: number | null
    sort_order?: number
    depends_on_step_id?: number | null
    checklist_template_id?: number | null
  }>
  checklist_items?: Array<{
    id: number
    label: string
    sort_order?: number
    required?: boolean
  }>
}

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
}

export const childTicketService = {
  async list(parentTicketId: string | number): Promise<ChildTicketTask[]> {
    const res = await apiClient.get<ApiResponse<ChildTicketTask[]>>(`/tickets/${parentTicketId}/tasks`)
    const data = res.data?.data ?? res.data
    return Array.isArray(data) ? data : []
  },

  async create(parentTicketId: string | number, payload: ChildTicketPayload): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(`/tickets/${parentTicketId}/tasks`, payload)
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to create task')
    }
    return res.data.data
  },

  async update(
    parentTicketId: string | number,
    taskId: number,
    payload: Partial<ChildTicketPayload>
  ): Promise<ChildTicketTask> {
    const res = await apiClient.put<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}`,
      payload
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to update task')
    }
    return res.data.data
  },

  async start(parentTicketId: string | number, taskId: number): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/start`
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to start task')
    }
    return res.data.data
  },

  async complete(
    parentTicketId: string | number,
    taskId: number,
    status?: string
  ): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/complete`,
      status ? { status } : {}
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to complete task')
    }
    return res.data.data
  },

  async completionPreflight(
    parentTicketId: string | number,
    taskId: number,
  ): Promise<TaskCompletionPreflight> {
    const res = await apiClient.get<ApiResponse<TaskCompletionPreflight>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/completion-preflight`,
    )
    const data = res.data?.data
    if (!data || typeof data.can_complete !== 'boolean' || !Array.isArray(data.blockers)) {
      throw new Error(res.data?.message ?? 'Failed to check task completion readiness')
    }
    return data
  },

  async skip(
    parentTicketId: string | number,
    taskId: number,
    reason?: string
  ): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/skip`,
      reason ? { reason } : {}
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to skip task')
    }
    return res.data.data
  },

  async cancelFeVisit(
    parentTicketId: string | number,
    taskId: number,
    reason?: string
  ): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/cancel-fe-visit`,
      reason ? { reason } : {}
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to cancel field visit')
    }
    return res.data.data
  },

  async forceCloseFeVisit(
    parentTicketId: string | number,
    taskId: number,
    reason?: string
  ): Promise<ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<ChildTicketTask>>(
      `/tickets/${parentTicketId}/tasks/${taskId}/force-close-fe-visit`,
      reason ? { reason } : {}
    )
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to force-close field visit')
    }
    return res.data.data
  },

  async reassignEngineer(
    parentTicketId: string | number,
    taskId: number,
    payload: {
      fe_user_id: number
      reason?: string
      schedule_date?: string
      schedule_end_date?: string | null
      schedule_time?: string
      schedule_end_time?: string
      latitude?: number | null
      longitude?: number | null
      location_address?: string | null
      contact_number_override?: string | null
    }
  ): Promise<{
    old_task: ChildTicketTask
    new_task: ChildTicketTask
    new_schedule: Record<string, unknown>
  }> {
    const res = await apiClient.post<
      ApiResponse<{
        old_task: ChildTicketTask
        new_task: ChildTicketTask
        new_schedule: Record<string, unknown>
      }>
    >(`/tickets/${parentTicketId}/tasks/${taskId}/reassign-engineer`, payload)
    if (!res.data?.data) {
      throw new Error(res.data?.message ?? 'Failed to reassign field engineer')
    }
    return res.data.data
  },

  async applyTemplate(parentTicketId: string | number, templateId: number): Promise<ChildTicketTask[]> {
    try {
      const res = await apiClient.post<ApiResponse<ChildTicketTask[]>>(
        `/tickets/${parentTicketId}/tasks/apply-template`,
        { template_id: templateId }
      )
      const data = res.data?.data ?? res.data
      if (!Array.isArray(data)) {
        throw new Error(res.data?.message ?? 'Failed to apply template')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to apply template'))
    }
  },

  async clearAll(
    parentTicketId: string | number,
    options?: { includeActive?: boolean; taskIds?: number[] },
  ): Promise<{ removed: number; skipped_active: number; skipped_task_titles: string[] }> {
    try {
      const res = await apiClient.post<
        ApiResponse<{ removed: number; skipped_active?: number; skipped_task_titles?: string[] }>
      >(`/tickets/${parentTicketId}/tasks/clear-all`, {
        include_active: Boolean(options?.includeActive),
        ...(options?.taskIds ? { task_ids: options.taskIds } : {}),
      })
      const data = res.data?.data
      if (!data || typeof data.removed !== 'number') {
        throw new Error(res.data?.message ?? 'Failed to reset workflow')
      }
      return {
        removed: data.removed,
        skipped_active: data.skipped_active ?? 0,
        skipped_task_titles: data.skipped_task_titles ?? [],
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to reset workflow'))
    }
  },

  async previewClearAll(parentTicketId: string | number): Promise<{
    safe: Array<{ id: number; title: string; status_label?: string | null }>
    active: Array<{ id: number; title: string; status_label?: string | null; reason: string }>
  }> {
    try {
      const res = await apiClient.get<
        ApiResponse<{
          safe: Array<{ id: number; title: string; status_label?: string | null }>
          active: Array<{ id: number; title: string; status_label?: string | null; reason: string }>
        }>
      >(`/tickets/${parentTicketId}/tasks/clear-all-preview`)
      const data = res.data?.data
      if (!data || !Array.isArray(data.safe) || !Array.isArray(data.active)) {
        throw new Error(res.data?.message ?? 'Failed to preview workflow reset')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to preview workflow reset'))
    }
  },

  async replaceTemplate(parentTicketId: string | number, templateId: number): Promise<ChildTicketTask[]> {
    try {
      const res = await apiClient.post<ApiResponse<ChildTicketTask[]>>(
        `/tickets/${parentTicketId}/tasks/replace-template`,
        { template_id: templateId }
      )
      const data = res.data?.data ?? res.data
      if (!Array.isArray(data)) {
        throw new Error(res.data?.message ?? 'Failed to replace template')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to replace template'))
    }
  },

  async listTemplates(params?: { support_type_id?: number }): Promise<TaskTemplate[]> {
    const res = await apiClient.get<ApiResponse<TaskTemplate[]>>('/helpdesk/task-templates', { params })
    const data = res.data?.data ?? res.data
    return Array.isArray(data) ? data : []
  },

  async closeValidation(parentTicketId: string | number): Promise<CloseValidationResult> {
    const res = await apiClient.get<ApiResponse<CloseValidationResult>>(
      `/tickets/${parentTicketId}/tasks/close-validation`
    )
    const data = res.data?.data
    if (!data || typeof data.can_close !== 'boolean') {
      throw new Error(res.data?.message ?? 'Failed to validate ticket close')
    }
    return {
      can_close: data.can_close,
      blocking_tasks: Array.isArray(data.blocking_tasks) ? data.blocking_tasks : [],
    }
  },
}
